import { SEO } from "@/components/seo/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const Terms = () => {
  return (
    <>
      <SEO 
        title="Terms of Service - QuickMedicalReview"
        description="Terms of Service for QuickMedicalReview - Read our terms and conditions for using our medical study platform."
        path="/terms"
      />
      
      <div className="container py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: October 9, 2025</p>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>1. Acceptance of Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                By accessing and using QuickMedicalReview ("the Service"), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Description of Service</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                QuickMedicalReview provides an online platform for medical students to practice multiple-choice questions (MCQs), access study notes, and track their learning progress. The Service includes both free trial access and paid subscription plans.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. Account Registration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>You must provide accurate and complete information when creating an account</li>
                <li>You are responsible for maintaining the security of your account credentials</li>
                <li>You must notify us immediately of any unauthorized access to your account</li>
                <li>One account per user; sharing accounts is prohibited</li>
                <li>You must be at least 13 years old to use this Service</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Subscription and Payment Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Free Trial</h3>
                <p className="text-muted-foreground">
                  New users receive a 3-day free trial. The trial automatically converts to a paid subscription unless cancelled before the trial period ends.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Subscription Plans</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>Monthly Plan: Billed monthly at the current rate</li>
                  <li>Quarterly Plan: Billed every 3 months</li>
                  <li>Biannual Plan: Billed every 6 months</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Billing</h3>
                <p className="text-muted-foreground">
                  Subscriptions automatically renew at the end of each billing period unless cancelled. Payment is processed through Stripe. You authorize us to charge your payment method for all fees incurred.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Price Changes</h3>
                <p className="text-muted-foreground">
                  We reserve the right to modify subscription prices. You will be notified at least 30 days before any price change. Continuing to use the Service after the price change constitutes acceptance.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Cancellation and Refund Policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Cancellation</h3>
                <p className="text-muted-foreground">
                  You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period. You will retain access until the paid period expires.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Refunds</h3>
                <p className="text-muted-foreground">
                  We do not provide refunds for partial months or unused portions of a subscription period. If you cancel during a free trial, you will not be charged. Refunds may be granted at our discretion in exceptional circumstances.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Acceptable Use</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-2">You agree NOT to:</p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>Share your account credentials with others</li>
                <li>Copy, reproduce, or distribute our content without permission</li>
                <li>Use automated tools to scrape or download content in bulk</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Upload malicious code or interfere with the Service's operation</li>
                <li>Impersonate others or provide false information</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>7. Intellectual Property</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                All content on QuickMedicalReview, including MCQs, notes, text, graphics, logos, and software, is the property of QuickMedicalReview or its content suppliers and is protected by copyright and intellectual property laws. You may not use, reproduce, or distribute any content without our written permission.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>8. Medical Disclaimer</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                The content provided on QuickMedicalReview is for educational purposes only and should not be considered medical advice. Always consult with qualified healthcare professionals for medical decisions. We do not guarantee the accuracy or completeness of any content. Use of this Service does not create a doctor-patient relationship.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                To the maximum extent permitted by law, QuickMedicalReview shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of the Service.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. Service Availability</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                We strive to provide continuous access to the Service but do not guarantee uninterrupted availability. We may suspend or terminate access for maintenance, updates, or technical issues. We are not liable for any downtime or service interruptions.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>11. Termination</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                We reserve the right to suspend or terminate your account at our discretion if you violate these Terms of Service or engage in fraudulent or abusive behavior. Upon termination, you will lose access to all content and data associated with your account.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>12. Changes to Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                We may modify these Terms of Service at any time. We will notify you of significant changes by email or through the Service. Continued use after changes constitutes acceptance of the modified terms.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>13. Governing Law</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law provisions.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>14. Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                For questions about these Terms of Service, please contact us at:{" "}
                <a href="mailto:support@quickmedicalreview.com" className="text-primary hover:underline">
                  support@quickmedicalreview.com
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Terms;
